import { useEffect, useState, useMemo } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { useHeaderAction } from '@/lib/header-action-context';
import { capabilityDisplayTitle, type Capability } from '@/types/capability-types';
import { getCapabilityColors, getStatusStyle, capabilityTitleIconBackground } from '@/lib/capability-colors';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Puzzle, Cable, Clock, Plus, Tag } from 'lucide-react';
import { Link } from 'react-router';
import { ConnectionSetupDrawer } from '@/components/integrations/connection-setup-drawer';
import type { IntegrationAuth } from '@/types/integration-form-types';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category' },
    { key: 'methods', label: 'Methods' },
];

const CATEGORIES = [
    { value: 'all', label: 'All Categories' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'communication', label: 'Communication' },
    { value: 'developer_tools', label: 'Developer Tools' },
    { value: 'crm', label: 'CRM' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'storage', label: 'Storage' },
];

/**
 * Extract integration-specific metadata from the capability's config/ext
 * fields. The list API returns limited data; the type_config details
 * (app, auth, method_map) are available in config or ext depending on
 * the gateway's response shape.
 */
function extractIntegrationMeta(item: Capability) {
    const tc = (item.config ?? item.ext ?? {}) as Record<string, unknown>;
    const app = tc.app as Record<string, string> | undefined;
    const auth = tc.auth as { supported_types?: string[]; strategies?: Record<string, unknown> } | undefined;
    const protocol = (tc.protocol as string) ?? undefined;
    const methodMap = tc.method_map as Record<string, unknown> | undefined;
    const methodCount = item.method_count ?? (methodMap ? Object.keys(methodMap).length : 0);
    const category = app?.category ?? item.tags?.find(t =>
        ['productivity', 'communication', 'developer_tools', 'crm', 'analytics', 'storage'].includes(t),
    );

    return {
        appName: app?.name,
        appVendor: app?.vendor,
        appCategory: category,
        appDescription: app?.description,
        appIconUrl: app?.icon_url,
        protocol,
        authTypes: auth?.supported_types ?? [],
        auth: auth as IntegrationAuth | undefined,
        methodCount,
    };
}

function IntegrationCardInner({
    item,
    meta,
    showDetail,
    descriptionLineClamp,
    largeText,
}: {
    item: Capability;
    meta: ReturnType<typeof extractIntegrationMeta>;
    showDetail: boolean;
    descriptionLineClamp: boolean;
    largeText?: boolean;
}) {
    const itemColors = getCapabilityColors('integration');
    const statusStyle = getStatusStyle(item.status);
    const dateStr = item.modified ?? item.created;

    const badgeText = largeText ? 'text-[9.9px]' : 'text-[9px]';
    const bodyText = largeText ? 'text-[11px]' : 'text-[10px]';
    const titleText = largeText ? 'text-[15.4px]' : 'text-sm';
    const iconSize = largeText ? 20 : 18;
    const inlineIconSize = largeText ? 11 : 10;

    const displayName = meta.appName ?? capabilityDisplayTitle(item);
    const description = meta.appDescription ?? item.description;

    return (
        <>
            {showDetail && (
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                    {item.latest_version && (
                        <span
                            className={`rounded bg-slate-100 px-1.5 py-0.5 font-mono text-slate-500 ${badgeText}`}
                        >
                            v{item.latest_version}
                        </span>
                    )}
                    {item.status && (
                        <span
                            className={`rounded-full px-1.5 py-0.5 font-medium uppercase ${badgeText} ${statusStyle}`}
                        >
                            {item.status}
                        </span>
                    )}
                </div>
            )}

            <div
                className={
                    showDetail
                        ? 'flex flex-row items-start gap-2'
                        : 'flex h-full min-h-0 w-full flex-row items-start gap-2 overflow-hidden'
                }
            >
                <div
                    className={`flex shrink-0 items-center justify-center rounded-md p-1.5 ${itemColors.bgLight} ${itemColors.text}`}
                >
                    {meta.appIconUrl ? (
                        <img
                            src={meta.appIconUrl}
                            alt=""
                            className="h-[18px] w-[18px] object-contain"
                            style={{ width: iconSize, height: iconSize }}
                        />
                    ) : (
                        <Puzzle size={iconSize} />
                    )}
                </div>
                <div
                    className={
                        showDetail
                            ? 'flex min-w-0 flex-1 flex-col'
                            : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
                    }
                >
                    <div
                        className={
                            showDetail
                                ? 'flex items-center gap-1.5'
                                : 'flex min-w-0 items-start gap-1.5'
                        }
                    >
                        <span
                            className={`min-w-0 text-left font-bold leading-tight text-slate-800 ${titleText} ${
                                !showDetail ? 'line-clamp-2' : ''
                            }`}
                        >
                            {displayName}
                        </span>
                    </div>
                    {showDetail && (
                        <span className={`text-left font-mono text-slate-500 ${bodyText}`}>
                            {item.fqcn || `${item.app_key}/${item.name}`}
                        </span>
                    )}
                    {showDetail && meta.appVendor && (
                        <span className={`text-left text-slate-500 ${bodyText}`}>
                            by {meta.appVendor}
                        </span>
                    )}
                    {description && (
                        <p
                            className={`text-left leading-tight text-slate-600 ${bodyText} ${
                                showDetail ? 'mt-2' : 'mt-2 min-h-0 flex-1'
                            } ${descriptionLineClamp ? 'line-clamp-2' : ''}`}
                        >
                            {description}
                        </p>
                    )}
                </div>
            </div>

            {showDetail && (
                <div
                    className={`mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-slate-500 ${bodyText}`}
                >
                    {meta.appCategory && (
                        <span className="flex items-center gap-1">
                            <Tag size={inlineIconSize} />
                            {meta.appCategory.replace(/_/g, ' ')}
                        </span>
                    )}
                    {meta.protocol && (
                        <span className="uppercase">{meta.protocol}</span>
                    )}
                    {meta.methodCount > 0 && (
                        <span>
                            {meta.methodCount} method
                            {meta.methodCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {meta.authTypes.length > 0 && (
                        <span>
                            {meta.authTypes.length} auth type
                            {meta.authTypes.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {dateStr && (
                        <span className="ml-auto flex items-center gap-1 text-slate-400">
                            <Clock size={inlineIconSize} />
                            {new Date(dateStr).toLocaleDateString()}
                        </span>
                    )}
                </div>
            )}
        </>
    );
}

const headerPrimaryButtonClass =
    'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

export default function IntegrationsGalleryPage() {
    usePageTitle('Integrations');
    const { setHeaderTitle, setHeaderAction } = useHeaderAction();

    const titleIcon = useMemo(() => {
        const colors = getCapabilityColors('integration');
        const iconSurfaceStyle = capabilityTitleIconBackground(colors.hex, true);
        return (
            <div
                className={`flex h-7 w-7 items-center justify-center rounded-md ${colors.text}`}
                style={iconSurfaceStyle}
            >
                <Puzzle size={16} />
            </div>
        );
    }, []);

    useEffect(() => {
        setHeaderTitle({
            icon: titleIcon,
            title: 'Integrations',
            subtitle: 'Browse available integrations and connect to external services.',
            intense: true,
        });
        setHeaderAction(
            <div className="ml-auto flex items-center gap-2">
                <Link to="/connections" className={headerPrimaryButtonClass}>
                    <Cable size={14} />
                    My Connections
                </Link>
            </div>,
        );
        return () => {
            setHeaderTitle(null);
            setHeaderAction(null);
        };
    }, [setHeaderTitle, setHeaderAction, titleIcon]);

    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [connectDialog, setConnectDialog] = useState<{
        open: boolean;
        name: string;
        appKey: string;
        integrationRef: string;
        auth: IntegrationAuth | undefined;
        typeConfig: Record<string, unknown> | undefined;
    }>({ open: false, name: '', appKey: '', integrationRef: '', auth: undefined, typeConfig: undefined });

    const { data, isLoading, error } = useCapabilityList(undefined, { type: 'integration' });

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const integrations = useMemo(() => {
        const items: Capability[] = data?.data ?? [];
        const filtered = items.filter((item) => {
            const meta = extractIntegrationMeta(item);
            const matchesSearch =
                !search ||
                item.name?.toLowerCase().includes(search.toLowerCase()) ||
                (item.display_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
                meta.appName?.toLowerCase().includes(search.toLowerCase()) ||
                item.description?.toLowerCase().includes(search.toLowerCase());
            const matchesCategory =
                category === 'all' || meta.appCategory === category;
            return matchesSearch && matchesCategory;
        });

        const sorted = [...filtered].sort((a, b) => {
            const metaA = extractIntegrationMeta(a);
            const metaB = extractIntegrationMeta(b);
            let cmp = 0;
            switch (sortField) {
                case 'name':
                    cmp = (metaA.appName ?? a.name ?? '').localeCompare(metaB.appName ?? b.name ?? '');
                    break;
                case 'category':
                    cmp = (metaA.appCategory ?? '').localeCompare(metaB.appCategory ?? '');
                    break;
                case 'methods':
                    cmp = metaA.methodCount - metaB.methodCount;
                    break;
                default:
                    cmp = (metaA.appName ?? a.name ?? '').localeCompare(metaB.appName ?? b.name ?? '');
                    break;
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [data, search, category, sortField, sortDirection]);

    const extraToolbar = (
        <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className={cn('w-[180px]', listControlsGlassTrigger)}>
                <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
                {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    if (error) {
        return (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
                Failed to load integrations. Please try again.
            </div>
        );
    }

    return (
        <>
            <CapabilityListPanel<Capability>
                items={integrations}
                sortOptions={SORT_OPTIONS}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                searchQuery={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search integrations..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                extraToolbar={extraToolbar}
                isLoading={isLoading}
                loadingMessage="Loading integrations..."
                emptyStateVariant="glass"
                emptyMessage={
                    search || category !== 'all'
                        ? 'Try adjusting your search or filters.'
                        : 'No integrations are registered yet.'
                }
                renderCard={(integration) => {
                    const meta = extractIntegrationMeta(integration);
                    const itemColors = getCapabilityColors('integration');
                    const cardBorder = `border border-slate-200 border-l-4 ${itemColors.border}`;

                    const connectBtn = (
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setConnectDialog({
                                    open: true,
                                    name: meta.appName ?? capabilityDisplayTitle(integration),
                                    appKey: integration.app_key,
                                    integrationRef: integration.name,
                                    auth: meta.auth,
                                    typeConfig: (integration.config ?? integration.ext ?? {}) as Record<string, unknown>,
                                });
                            }}
                            className="mt-auto flex w-full items-center justify-center gap-1.5 rounded-md bg-violet-100 px-3 py-1.5 text-[10px] font-medium text-violet-700 transition-colors hover:bg-violet-200"
                        >
                            <Plus className="h-3 w-3" />
                            Connect
                        </button>
                    );

                    return (
                        <Link
                            key={integration.name}
                            to={`/integrations/${encodeURIComponent(integration.app_key)}/${encodeURIComponent(integration.name)}`}
                            className="group/card relative z-0 block hover:z-40 focus-within:z-40"
                        >
                            <div
                                className={`relative z-0 flex h-[130px] min-h-[130px] w-full cursor-pointer flex-col overflow-hidden rounded-lg ${cardBorder} bg-white p-3 shadow-sm transition-shadow duration-150 group-hover/card:shadow-none group-focus-within/card:shadow-none hover:border-slate-300 hover:bg-slate-50 focus-within:border-slate-300 focus-within:bg-slate-50`}
                            >
                                <IntegrationCardInner
                                    item={integration}
                                    meta={meta}
                                    showDetail={false}
                                    descriptionLineClamp
                                />
                                {connectBtn}
                            </div>

                            <div
                                className="pointer-events-none absolute left-0 top-0 z-10 min-h-[120%] w-[120%] min-w-0 opacity-0 transition-opacity duration-150 group-hover/card:pointer-events-auto group-hover/card:opacity-100 group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100"
                            >
                                <div
                                    className="flex min-h-[130px] w-full cursor-pointer flex-col rounded-xl border-2 border-solid bg-white p-4 hover:bg-slate-50"
                                    style={{
                                        borderColor: itemColors.hex,
                                        boxShadow: `0 25px 50px -12px rgb(15 23 42 / 0.2), 0 12px 36px -12px color-mix(in srgb, ${itemColors.hex} 38%, transparent)`,
                                    }}
                                >
                                    <IntegrationCardInner
                                        item={integration}
                                        meta={meta}
                                        showDetail
                                        descriptionLineClamp={false}
                                        largeText
                                    />
                                    {connectBtn}
                                </div>
                            </div>
                        </Link>
                    );
                }}
            />

            <ConnectionSetupDrawer
                open={connectDialog.open}
                onOpenChange={(open) => setConnectDialog({ ...connectDialog, open })}
                integrationName={connectDialog.name}
                appKey={connectDialog.appKey}
                integrationRef={connectDialog.integrationRef}
                auth={connectDialog.auth}
                typeConfig={connectDialog.typeConfig}
            />
        </>
    );
}
